import React, { cloneElement } from 'react';
import {
  Breadcrumb as ChakraBreadcrumb,
  BreadcrumbProps as ChakraBreadcrumbProps,
  BreadcrumbSeparator,
  forwardRef,
} from '@chakra-ui/react';
import {
  ColorMode,
  useColorMode,
} from '@allergan-data-labs/alle-elements-core';
import { BreadcrumbOverflowMenu } from './breadcrumb-overflow-menu';
import { getSeparatorPaddingStyles } from './breadcrumb.theme';

export interface BreadcrumbsProps extends ChakraBreadcrumbProps {
  children?: React.ReactNode;
  /**
   * Determines if the last child should be displayed
   * @default 'false'
   */
  showLastChild?: boolean;
  /**
   * Determines if the overflow menu should be displayed
   * @default 'false'
   */
  showOverflowMenu?: boolean;
  /** The color mode that the button will display.
   * @default light
   */
  colorMode?: ColorMode;
}

const isLastChild = (
  child: React.ReactNode,
  index: number,
  array: React.ReactNode[]
): boolean => React.isValidElement(child) && index === array.length - 1;

const setLastChildAsCurrentPage = (
  child: React.ReactNode,
  index: number,
  array: React.ReactNode[]
): React.ReactNode =>
  isLastChild(child, index, array)
    ? cloneElement(child as React.ReactElement, { isCurrentPage: true })
    : child;

/**
 * Used to provide a navigation trail for users to follow back to the starting or entry point.
 **/
export const Breadcrumb = forwardRef<BreadcrumbsProps, 'nav'>(
  (
    {
      children,
      showLastChild = true,
      showOverflowMenu = false,
      colorMode: propColorMode,
      ...props
    },
    ref
  ) => {
    const { colorMode } = useColorMode(propColorMode);

    const childrenArray = React.Children.toArray(children);
    const childrenWithProps = childrenArray.map(setLastChildAsCurrentPage);
    const firstItem = childrenWithProps[0];
    const lastItem = childrenWithProps[childrenWithProps.length - 1];
    const overflowItems = childrenWithProps.slice(1, -1);

    return (
      <ChakraBreadcrumb ref={ref} colorScheme={colorMode} {...props}>
        {showOverflowMenu && childrenWithProps.length > 2 ? (
          <>
            {firstItem}
            <BreadcrumbSeparator spacing={getSeparatorPaddingStyles()}>
              /
            </BreadcrumbSeparator>
            <BreadcrumbOverflowMenu overflowItems={overflowItems} />
            <BreadcrumbSeparator spacing={getSeparatorPaddingStyles()}>
              /
            </BreadcrumbSeparator>
            {showLastChild && lastItem}
          </>
        ) : showLastChild ? (
          childrenWithProps
        ) : (
          childrenWithProps.slice(0, -1)
        )}
      </ChakraBreadcrumb>
    );
  }
);

export {
  BreadcrumbItem,
  BreadcrumbSeparator,
  BreadcrumbLink,
} from '@chakra-ui/react';
